import { Bookmark, Eye, Highlighter, Loader2, Network, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import type { ToolCardActionsModel } from '@/features/agent/tools/useToolCardActions'
import type { ToolActionHandlers, ToolActionKind } from './chat-tool-actions'

export type { ToolActionHandlers } from './chat-tool-actions'

interface ToolCardActionsProps {
  /** Resolved actions from `useToolCardActions`. The parent renders this
   *  component only when the hook returned a non-null model. */
  model: ToolCardActionsModel
  /** Handlers keyed by action kind — wired to the resolved primary/secondary. */
  handlers: ToolActionHandlers
  /** Layout flag: when the parent footer renders the hint itself (e.g.
   *  FindingCard), skip this component's leading hint/spacer slot so the
   *  buttons sit flush. The hint text is already empty in that case. */
  hideHint?: boolean
  /** Action whose handler is currently running. Its button shows a spinner in
   *  place of its icon; while any action is pending every button is disabled so
   *  async surfaces (e.g. an import-then-apply) can't fire twice. Null/omitted
   *  leaves the footer fully interactive. */
  pending?: ToolActionKind | null
}

/** Icon per action kind. Exported so per-row action strips (e.g. the
 *  find-entity candidate rows) paint the same glyph as this footer. */
export const TOOL_ACTION_ICONS: Record<ToolActionKind, typeof Plus> = {
  // Distinct from Add (+) — these open / replace the view rather than
  // appending to it. Network glyph nods at the graph itself.
  showOnGraph: Network,
  showSeparately: Network,
  addToGraph: Plus,
  highlight: Highlighter,
  saveAsInvestigation: Bookmark,
}

/** i18n key per action kind. Exported alongside `TOOL_ACTION_ICONS` so every
 *  surface offering these actions uses one set of labels. */
export const TOOL_ACTION_LABEL_KEYS: Record<ToolActionKind, string> = {
  showOnGraph: 'chat.toolActions.showOnGraph',
  showSeparately: 'chat.toolActions.showSeparately',
  addToGraph: 'chat.toolActions.addToGraph',
  highlight: 'chat.toolActions.highlight',
  saveAsInvestigation: 'chat.toolActions.saveAsInvestigation',
}

/**
 * Footer actions for agent tool-result cards — pure presentation. The
 * primary-vs-secondary decision and the hint copy are resolved upstream by
 * `useToolCardActions`; this component just paints the chosen actions.
 *
 * Renders at most two buttons (primary CTA + one secondary). More than two
 * competing buttons turns the card footer into a toolbar and obscures the
 * recommended action.
 */
export function ToolCardActions({ model, handlers, hideHint, pending }: ToolCardActionsProps) {
  const { t } = useTranslation()
  const { primary, secondary, hint } = model
  const secondaryKind = secondary[0] ?? null

  const PrimaryIcon = TOOL_ACTION_ICONS[primary]
  const primaryLabel = t(TOOL_ACTION_LABEL_KEYS[primary])
  // One action runs at a time; disable the whole footer while it does so a
  // second click can't kick off a concurrent import/apply.
  const busy = pending != null

  return (
    // Phone: the hint takes its own full line (no hover tooltip to read a
    // truncated one) and the buttons sit right-aligned underneath.
    <div className="flex items-center justify-between gap-2 max-md:flex-wrap">
      {hint ? (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="min-w-0 flex-1 cursor-default truncate text-[11px] text-muted-foreground max-md:basis-full max-md:whitespace-normal">
                {hint}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">{hint}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : hideHint ? null : (
        // Spacer that pushes the buttons right when this component owns the
        // full-width footer. Suppressed when the parent renders its own hint
        // slot (hideHint), to avoid a double spacer.
        <span className="min-w-0 flex-1" />
      )}
      <div className="flex shrink-0 items-center gap-1.5 max-md:ml-auto max-md:min-w-0 max-md:shrink max-md:flex-wrap max-md:justify-end">
        {secondaryKind && handlers[secondaryKind] && (
          <button
            type="button"
            onClick={handlers[secondaryKind]}
            disabled={busy}
            className="inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-border bg-background px-2.5 text-[11px] font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50 max-md:h-9 max-md:px-3 max-md:text-xs"
          >
            {pending === secondaryKind ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              (() => {
                const SecondaryIcon = TOOL_ACTION_ICONS[secondaryKind]
                return <SecondaryIcon className="size-3.5" />
              })()
            )}
            {t(TOOL_ACTION_LABEL_KEYS[secondaryKind])}
          </button>
        )}
        <button
          type="button"
          onClick={handlers[primary]}
          disabled={busy}
          className="inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary px-3 text-[11px] font-medium text-primary-foreground shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50 max-md:h-9 max-md:text-xs"
        >
          {pending === primary ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <PrimaryIcon className="size-3.5" />
          )}
          {primaryLabel}
        </button>
      </div>
    </div>
  )
}

/** Re-export for callers that still need the placeholder eye icon (e.g. when
 *  custom-rendering outside this component). */
export { Eye as ToolCardEyeIcon }
